import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Form,
  Row,
  Col,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import { useToastMessages } from '../../../../../../hooks/useToastMessages.jsx';
import {
  useGetPhdSystemStateCurrentQuery,
  useGetPhdSystemStatesQuery,
  useSavePhdSystemStateMutation,
} from '../../../../../../redux/services/phd.jsx';
import checkPermission from '../../../../../../utils/helpers/checkPermission';
import useIsUserPhdAdmin from '../../../../../../hooks/useIsUserPhdAdmin.jsx';
import { Spinner } from '../../../../../common';
import PhdSettingsSection from './PhdSettingsSection.jsx';

const formatDateTimeForApi = (value) =>
  value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : null;

const PhdSystemStateSettingsSection = () => {
  const { t } = useTranslation();
  const [historyPagination, setHistoryPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const { data: currentState, isLoading: isLoadingCurrent } =
    useGetPhdSystemStateCurrentQuery();
  const {
    data: states,
    isLoading: isLoadingHistory,
    refetch,
  } = useGetPhdSystemStatesQuery({
    page: historyPagination.current,
    per_page: historyPagination.pageSize,
  });
  const [saveState, { isLoading: isSaving }] = useSavePhdSystemStateMutation();
  const { saveSuccess, saveFailed } = useToastMessages();
  const user = useSelector((state) => state.auth.user);
  const isAdmin = useIsUserPhdAdmin();
  const canEditState = checkPermission(
    user,
    'phd-system-states.store',
    isAdmin,
  );
  const [form] = Form.useForm();

  const initialValues = useMemo(
    () => ({
      is_open: currentState?.is_open ?? false,
      date_opening: currentState?.date_opening
        ? dayjs(currentState.date_opening)
        : null,
      date_closing: currentState?.date_closing
        ? dayjs(currentState.date_closing)
        : null,
    }),
    [currentState],
  );

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [form, initialValues]);

  const systemStateMessage = useMemo(() => {
    if (!currentState) {
      return t('Stav systému zatiaľ nebol nastavený.');
    }

    if (currentState.is_open) {
      return currentState.date_closing
        ? t('Systém je otvorený do {{date}}', {
            date: dayjs(currentState.date_closing).format('DD.MM.YYYY HH:mm'),
          })
        : t('Systém je otvorený bez plánovaného ukončenia.');
    }

    return currentState.date_opening
      ? t('Systém sa otvorí {{date}}', {
          date: dayjs(currentState.date_opening).format('DD.MM.YYYY HH:mm'),
        })
      : t('Systém je zatvorený bez plánovaného otvorenia.');
  }, [currentState, t]);

  const watchedValues = Form.useWatch([], form);

  const hasUnsavedChanges = useMemo(() => {
    if (!watchedValues) {
      return false;
    }

    const datesDiffer = (first, second) => {
      if (!first && !second) {
        return false;
      }
      if (!first || !second) {
        return true;
      }

      return !dayjs(first).isSame(dayjs(second));
    };

    return (
      initialValues.is_open !== watchedValues.is_open ||
      datesDiffer(initialValues.date_opening, watchedValues.date_opening) ||
      datesDiffer(initialValues.date_closing, watchedValues.date_closing)
    );
  }, [initialValues, watchedValues]);

  const submitPayload = async (payload) => {
    if (!canEditState) {
      return;
    }

    try {
      await saveState({ body: payload }).unwrap();
      setHistoryPagination((prev) => ({ ...prev, current: 1 }));
      saveSuccess();
      refetch();
    } catch (error) {
      saveFailed(error);
    }
  };

  const handleSubmit = (values) => {
    if (!canEditState || !hasUnsavedChanges) {
      return;
    }

    submitPayload({
      is_open: values.is_open,
      date_opening: formatDateTimeForApi(values.date_opening),
      date_closing: formatDateTimeForApi(values.date_closing),
    });
  };

  const handleHistoryPageChange = (page, pageSize) => {
    setHistoryPagination({
      current: page,
      pageSize,
    });
  };

  const historyColumns = [
    {
      title: t('Vytvorené'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (value) =>
        value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '—',
    },
    {
      title: t('Naposledy upravené'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (value) =>
        value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '—',
    },
    {
      title: t('Stav'),
      dataIndex: 'is_open',
      key: 'is_open',
      render: (value) =>
        value ? (
          <Tag color="green">{t('Otvorené')}</Tag>
        ) : (
          <Tag color="red">{t('Zatvorené')}</Tag>
        ),
    },
    {
      title: t('Plánované otvorenie'),
      dataIndex: 'date_opening',
      key: 'date_opening',
      render: (value) =>
        value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '—',
    },
    {
      title: t('Plánované uzatvorenie'),
      dataIndex: 'date_closing',
      key: 'date_closing',
      render: (value) =>
        value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '—',
    },
    {
      title: t('Upravil'),
      dataIndex: 'updated_by_name',
      key: 'updated_by_name',
      render: (value) => value || '—',
    },
  ];

  if (isLoadingCurrent || isLoadingHistory) {
    return <Spinner />;
  }

  return (
    <PhdSettingsSection title={t('Správa vytvárania tém dizertačných prác')}>
      <Card title={t('Nastavenie dostupnosti')} style={{ marginBottom: 16 }}>
        <Space orientation="vertical" size={16} style={{ width: '100%' }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <div>
              <Typography.Text strong>{t('Súčasný stav')}</Typography.Text>
              <div style={{ marginTop: 4 }}>
                {currentState ? (
                  currentState.is_open ? (
                    <Tag color="green">{t('Vytváranie tém je povolené')}</Tag>
                  ) : (
                    <Tag color="red">{t('Vytváranie tém je zakázané')}</Tag>
                  )
                ) : (
                  '—'
                )}
              </div>
            </div>
            <Typography.Text
              type="secondary"
              style={{ fontSize: 12, lineHeight: 1.35 }}
            >
              {systemStateMessage}
            </Typography.Text>
          </div>

          <Form
            layout="vertical"
            initialValues={initialValues}
            onFinish={handleSubmit}
            key={currentState?.id || 'new'}
            form={form}
          >
            <Form.Item
              name="is_open"
              label={t('Povoliť vytváranie tém')}
              valuePropName="checked"
            >
              <Switch
                checkedChildren={t('Povolené')}
                unCheckedChildren={t('Zakázané')}
                disabled={!canEditState}
              />
            </Form.Item>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  noStyle
                  shouldUpdate={(prev, curr) => prev.is_open !== curr.is_open}
                >
                  {({ getFieldValue }) => (
                    <Form.Item
                      name="date_opening"
                      label={t('Automaticky povoliť od')}
                      extra={t(
                        'Dostupné iba ak je vytváranie tém momentálne zakázané.',
                      )}
                    >
                      <DatePicker
                        showTime
                        style={{ width: '100%' }}
                        disabled={!canEditState || getFieldValue('is_open')}
                      />
                    </Form.Item>
                  )}
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="date_closing"
                  label={t('Automaticky zakázať od')}
                  extra={t(
                    'Voliteľné. Po tomto termíne sa vytváranie tém uzatvorí.',
                  )}
                >
                  <DatePicker
                    showTime
                    style={{ width: '100%' }}
                    disabled={!canEditState}
                  />
                </Form.Item>
              </Col>
            </Row>

            {hasUnsavedChanges && canEditState ? (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                title={t('Máte neuložené zmeny.')}
              />
            ) : null}
            {!hasUnsavedChanges && canEditState ? (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                title={t('Formulár je zhodný s aktuálnym nastavením.')}
              />
            ) : null}
            <Button
              type="primary"
              htmlType="submit"
              loading={isSaving}
              disabled={!canEditState || !hasUnsavedChanges}
            >
              {t('Uložiť nastavenie')}
            </Button>
          </Form>
        </Space>
      </Card>

      <Card title={t('História zmien')}>
        <Table
          rowKey={(record) => record.id}
          dataSource={Array.isArray(states?.data) ? states.data : []}
          columns={historyColumns}
          pagination={{
            current: states?.current_page ?? historyPagination.current,
            pageSize: states?.per_page ?? historyPagination.pageSize,
            total: states?.total ?? 0,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: handleHistoryPageChange,
            onShowSizeChange: handleHistoryPageChange,
          }}
          size="small"
        />
      </Card>
    </PhdSettingsSection>
  );
};

export default PhdSystemStateSettingsSection;
